
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, PiggyBank, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ZXingScanner } from '../../components/ZXingScanner';
import { API } from '../../utils/api';
import { useAuth } from '@/context/AuthContext';

export function MobPayHome({ parents, selectedParent, setSelectedParent, amount, setAmount, message, setMessage, staticPayee, setStaticPayee, externalToken, externalDeeplink, createRequest, scanningPayment, setScanningPayment, onScanPayment, headers, analytics, myRequests = [] }) {
  const { user } = useAuth();
  const [vendorCheck, setVendorCheck] = useState(null); // null, 'checking', {exists, mode, message}
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [includeLocation, setIncludeLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [locLoading, setLocLoading] = useState(false);
  const [aliasInfo, setAliasInfo] = useState(null);
  const [aliasQr, setAliasQr] = useState('');
  const [aliasLoading, setAliasLoading] = useState(false);
  const [allowedMerchants, setAllowedMerchants] = useState([]);
  const [paymigQr, setPaymigQr] = useState('');
  const [stepsToday, setStepsToday] = useState(0);
  
  const hasActiveParents = parents && parents.length > 0;

  const fetchCategories = async () => {
    if (!selectedParent) {
      setCategories([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/mobpay/parent-categories/${selectedParent}`, { headers });
      setCategories(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch categories', e);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [selectedParent]);

  const buildAliasQr = async (qrData) => {
    if (!qrData) {
      setAliasQr('');
      return;
    }
    const QRCode = require('qrcode');
    const qr = await QRCode.toDataURL(JSON.stringify(qrData));
    setAliasQr(qr);
  };

  const fetchAlias = async () => {
    if (!headers?.Authorization) return;
    setAliasLoading(true);
    try {
      const res = await axios.get(`${API}/alias/me`, { headers });
      if (res.data?.has_alias) {
        setAliasInfo(res.data);
        await buildAliasQr(res.data.qr_data);
      } else {
        setAliasInfo(null);
        setAliasQr('');
      }
    } catch (e) {
      console.error('Failed to fetch alias', e);
      toast.error('Failed to load alias');
    } finally {
      setAliasLoading(false);
    }
  };

  const handleCreateAlias = async () => {
    try {
      const res = await axios.post(`${API}/alias/create`, {
        nickname: aliasInfo?.nickname || undefined
      }, { headers });
      setAliasInfo(res.data);
      await buildAliasQr(res.data.qr_data);
      toast.success('Alias created');
    } catch (e) {
      console.error('Failed to create alias', e);
      toast.error(e.response?.data?.detail || 'Failed to create alias');
    }
  };

  const handleCopyAlias = async () => {
    if (!aliasInfo?.display_name && !aliasInfo?.alias) return;
    const text = aliasInfo.display_name || aliasInfo.alias;
    await navigator.clipboard.writeText(text);
    toast.success('Alias copied');
  };

  useEffect(() => {
    fetchAlias();
  }, [headers?.Authorization]);

  useEffect(() => {
    const fetchAllowed = async () => {
      try {
        const res = await axios.get(`${API}/mobpay/allowed-merchants`, { headers });
        setAllowedMerchants(res.data.allowed_merchants || []);
      } catch (e) {
        console.error('Failed to load allowed merchants');
      }
    };
    fetchAllowed();
  }, [headers?.Authorization]);

  useEffect(() => {
    const buildPaymigQr = async () => {
      if (!user?.paymig_id) return;
      const QRCode = require('qrcode');
      const qrData = JSON.stringify({ type: 'paymig_id', paymig_id: user.paymig_id });
      const qr = await QRCode.toDataURL(qrData, { width: 140, margin: 1 });
      setPaymigQr(qr);
    };
    buildPaymigQr();
  }, [user?.paymig_id]);

  useEffect(() => {
    const fetchSteps = async () => {
      if (user?.buddy_type !== 'minor') return;
      try {
        const res = await axios.get(`${API}/watch/steps/today`, { headers });
        setStepsToday(res.data.steps || 0);
      } catch (e) {
        setStepsToday(0);
      }
    };
    fetchSteps();
  }, [user?.buddy_type]);

  // Handle Location Toggle
  useEffect(() => {
    if (includeLocation) {
      setLocLoading(true);
      if (!navigator.geolocation) {
        toast.error('Geolocation is not supported by your browser');
        setIncludeLocation(false);
        setLocLoading(false);
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = `${position.coords.latitude},${position.coords.longitude}`;
          setLocation(loc);
          setLocLoading(false);
          toast.success('Location acquired');
        },
        (error) => {
          console.error(error);
          toast.error('Unable to retrieve location');
          setIncludeLocation(false);
          setLocLoading(false);
        }
      );
    } else {
      setLocation(null);
      setLocLoading(false);
    }
  }, [includeLocation]);
  
  // Check if payee is in vendor registry
  const checkVendorRegistry = async (payeeNumber) => {
    if (!payeeNumber || payeeNumber.length < 9) {
      setVendorCheck(null);
      return;
    }
    
    setVendorCheck('checking');
    
    try {
      // Normalize the number for validation
      let normalized = payeeNumber.trim();
      
      // Valid Swish number formats:
      // 1. Merchant aliases: 123xxxxxxx or 987xxxxxxx (10-13 digits)
      // 2. Swedish mobile with country code: +46xxxxxxxxx or 46xxxxxxxxx
      // 3. Swedish mobile local format: 07xxxxxxxx (10 digits)
      // 4. Any phone number format for App2App
      
      const isMerchantAlias = /^(123|987)\d{7,10}$/.test(normalized);
      const isSwedishWithPlus = /^\+46\d{9,10}$/.test(normalized);
      const isSwedishWithoutPlus = /^46\d{9,10}$/.test(normalized);
      const isSwedishLocal = /^0\d{9,10}$/.test(normalized); // 07xxxxxxxx format
      
      const isValidFormat = isMerchantAlias || isSwedishWithPlus || isSwedishWithoutPlus || isSwedishLocal;
      
      if (!isValidFormat) {
        setVendorCheck({ exists: false, mode: 'INVALID', message: 'Invalid Swish number format. Use +46..., 07..., or merchant alias (123/987...)' });
        return;
      }
      
      const isRegistered = allowedMerchants.includes(normalized);

      if (!isRegistered) {
        setVendorCheck({
          exists: false,
          mode: 'INVALID',
          message: 'Swish number not in commercial allowlist',
          swish_number: normalized
        });
        return;
      }

      if (isRegistered) {
        setVendorCheck({ 
          exists: true, 
          mode: 'INTERNAL', 
          message: 'Registered commercial merchant',
          swish_number: normalized
        });
      }
    } catch (e) {
      setVendorCheck({ exists: false, mode: 'APP2APP', message: 'Using App2App flow' });
    }
  };
  
  // Debounced vendor check
  useEffect(() => {
    if (staticPayee) {
      const timer = setTimeout(() => checkVendorRegistry(staticPayee), 600);
      return () => clearTimeout(timer);
    } else {
      setVendorCheck(null);
    }
  }, [staticPayee]);
  
  const getTitle = () => {
    if (externalToken || externalDeeplink) return 'Send Vendor Token to Payer';
    if (staticPayee && vendorCheck?.mode === 'INVALID') return 'Invalid Payee';
    if (staticPayee && vendorCheck?.mode === 'INTERNAL') return 'Payment to Registered Vendor';
    if (staticPayee && vendorCheck?.mode === 'STATIC') return 'Payment to Other Merchant';
    if (staticPayee) return 'Send Payment to Merchant';
    return 'Send Payment Request';
  };
  
  const getDescription = () => {
    if (externalToken || externalDeeplink) return 'Forward vendor token to payer for approval';
    if (staticPayee && vendorCheck?.mode === 'INVALID') return vendorCheck.message;
    if (staticPayee && vendorCheck?.mode === 'INTERNAL') return 'Using INTERNAL flow with callbacks';
    if (staticPayee && vendorCheck?.mode === 'STATIC') return 'Using STATIC flow (payer confirms manually)';
    if (staticPayee) return 'Enter merchant Swish number or scan QR';
    return 'Charge payer\'s merchant account';
  };

  const getButtonText = () => {
    if (externalToken || externalDeeplink) return 'Send Token to Payer';
    if (staticPayee && vendorCheck?.mode === 'INVALID') return 'Invalid Payee';
    if (staticPayee && vendorCheck?.mode === 'INTERNAL') return 'Send Request to Payer (Registered)';
    if (staticPayee && vendorCheck?.mode === 'STATIC') return 'Send Request to Payer (Static)';
    if (staticPayee) return 'Send Request to Payer';
    return 'Send Request to Payer';
  };

  const handleCreateRequest = () => {
    if (vendorCheck?.mode === 'INVALID') {
      toast.error(vendorCheck.message || 'Invalid payee');
      return;
    }
    // Pass category name and location to createRequest
    createRequest({ 
      category_name: selectedCategory,
      location: location
    });
  };

  // Calculate progress percentage safely
  const monthSpent = analytics?.month_spent || 0;
  const monthLimit = analytics?.month_limit || 1;
  const progressPercent = Math.min(100, (monthSpent / monthLimit) * 100);
  const isOverLimit = progressPercent > 75;

  // Filter today's requests
  const todayRequests = myRequests.filter(r => {
    try {
      const reqDate = new Date(r.created_at);
      const today = new Date();
      return reqDate.toDateString() === today.toDateString();
    } catch {
      return false;
    }
  });

  const formatTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };
  
  return (
    <div className="space-y-6" data-testid="mobpay-home">
      {/* Widgets Section */}
      <div className={`grid grid-cols-1 ${user?.buddy_type === 'minor' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`} data-testid="buddy-analytics-grid">
        {/* Today Widget */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100" data-testid="buddy-spent-today-card">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <div className="text-sm text-blue-600 font-medium mb-1 flex items-center gap-2">
              <Coins className="w-4 h-4 animate-bounce" /> Spent Today
            </div>
            <div className="text-3xl font-bold text-blue-900 animate-in fade-in zoom-in duration-500" data-testid="buddy-spent-today-value">
              {analytics?.today_spent || 0} <span className="text-sm font-normal text-blue-700">SEK</span>
            </div>
          </CardContent>
        </Card>

        {/* Month Meter Widget */}
        <Card className="bg-white border-gray-200" data-testid="buddy-month-spending-card">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-medium text-gray-600">{analytics?.month_name || 'Month'} Spending</div>
              <div className={`text-xs font-bold ${isOverLimit ? 'text-red-600' : 'text-green-600'}`} data-testid="buddy-month-spending-percent">
                {Math.round(progressPercent)}%
              </div>
            </div>
            <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-1000 ease-out ${isOverLimit ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span data-testid="buddy-month-spent-value">{monthSpent} SEK</span>
              <span data-testid="buddy-month-limit-value">Limit: {monthLimit} SEK</span>
            </div>
          </CardContent>
        </Card>

        {/* Saved Widget */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100" data-testid="buddy-saved-card">
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <div className="text-sm text-emerald-600 font-medium mb-1 flex items-center gap-2">
              <PiggyBank className="w-4 h-4 animate-bounce" /> {user?.buddy_type === 'minor' ? 'Piggy Bank' : 'Saving Account'}
            </div>
            <div className="text-3xl font-bold text-emerald-900 animate-in fade-in zoom-in duration-700" data-testid="buddy-saved-value">
              {analytics?.month_saved || 0} <span className="text-sm font-normal text-emerald-700">SEK</span>
            </div>
          </CardContent>
        </Card>
        {user?.buddy_type === 'minor' && (
          <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-100" data-testid="buddy-steps-card">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <div className="text-sm text-blue-600 font-medium mb-1">Steps Today</div>
              <div className="text-3xl font-bold text-blue-900" data-testid="buddy-steps-value">
                {stepsToday}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="border-slate-200 bg-slate-50" data-testid="buddy-paymig-id-card">
        <CardHeader>
          <CardTitle className="text-base">Your Paymig ID</CardTitle>
          <CardDescription>Share this Paymig ID or QR code to receive payments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex-1 space-y-2">
              <div className="text-xs text-muted-foreground">Paymig ID</div>
              <div className="text-2xl font-bold" data-testid="buddy-paymig-id-value">
                {user?.paymig_id || '—'}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => navigator.clipboard.writeText(user?.paymig_id || '')} data-testid="buddy-paymig-id-copy">
                  Copy Paymig ID
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              {paymigQr ? (
                <img
                  src={paymigQr}
                  alt="paymig-id-qr"
                  className="h-32 w-32 border rounded-lg bg-white"
                  data-testid="buddy-paymig-id-qr"
                />
              ) : (
                <div className="text-xs text-muted-foreground" data-testid="buddy-paymig-id-qr-missing">QR not available</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Buddy Alias Card */}
      <Card className="border-teal-200 bg-teal-50" data-testid="buddy-alias-card">
        <CardHeader>
          <CardTitle className="text-base">Your Buddy Alias</CardTitle>
          <CardDescription>Share this alias or QR code to receive gifts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {aliasLoading ? (
            <p className="text-sm text-muted-foreground" data-testid="buddy-alias-loading">Loading alias...</p>
          ) : aliasInfo ? (
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="flex-1 space-y-2">
                <div className="text-xs text-muted-foreground">Alias</div>
                <div className="text-2xl font-bold" data-testid="buddy-alias-value">
                  {aliasInfo.display_name || aliasInfo.alias}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCopyAlias} data-testid="buddy-alias-copy-btn">
                    Copy Alias
                  </Button>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                {aliasQr ? (
                  <img
                    src={aliasQr}
                    alt="buddy-alias-qr"
                    className="h-32 w-32 border rounded-lg bg-white"
                    data-testid="buddy-alias-qr"
                  />
                ) : (
                  <div className="text-xs text-muted-foreground" data-testid="buddy-alias-qr-missing">QR not available</div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
              <div>
                <div className="font-medium">No alias yet</div>
                <p className="text-sm text-muted-foreground">Generate a 10‑digit Buddy alias for gifts.</p>
              </div>
              <Button onClick={handleCreateAlias} data-testid="buddy-alias-create-btn">
                Generate Alias
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <h2 className="text-xl font-semibold" data-testid="buddy-send-request-title">Send Payment Request</h2>
      
      {!hasActiveParents && (
        <Card className="border-yellow-500 bg-yellow-50" data-testid="buddy-no-payers-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <div className="font-semibold text-yellow-900" data-testid="buddy-no-payers-title">No Active Payers</div>
                <p className="text-sm text-yellow-800 mt-1">
                  You don't have any active payer bindings. Ask your payer to enable your account in their Settings, or scan a new binding QR code in Settings tab.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card className={!hasActiveParents ? 'opacity-50' : ''}>
        <CardHeader>
          <CardTitle>{getTitle()}</CardTitle>
          <CardDescription>{getDescription()}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Payer</Label>
            <select 
              data-testid="payer-select" 
              className="w-full border rounded-md px-3 py-2" 
              value={selectedParent} 
              onChange={(e)=>setSelectedParent(e.target.value)}
              disabled={!hasActiveParents}
            >
              <option value="">Select...</option>
              {parents.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.nickname || p.email})</option>)}
            </select>
            {!hasActiveParents && (
              <p className="text-xs text-red-600 mt-1" data-testid="buddy-no-payers-available">No active payers available</p>
            )}
          </div>
          
          {/* Scan Button */}
          <div className="flex items-center gap-3">
            <Button 
              data-testid="scan-payment-qr" 
              type="button" 
              variant="outline" 
              onClick={()=>setScanningPayment(true)}
              disabled={!hasActiveParents}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              Scan Merchant QR Code
            </Button>
            {(externalToken || externalDeeplink) && (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border" data-testid="card-captured-token">
                {externalDeeplink ? 'Deeplink' : 'Token'}
              </span>
            )}
            {staticPayee && vendorCheck && vendorCheck !== 'checking' && (
              <span className={`text-xs px-2 py-1 rounded-full border ${
                vendorCheck.mode === 'INTERNAL' ? 'bg-green-50 text-green-800' :
                vendorCheck.mode === 'APP2APP' ? 'bg-teal-50 text-teal-800' :
                vendorCheck.mode === 'STATIC' ? 'bg-yellow-50 text-yellow-800' :
                'bg-red-50 text-red-800'
              }`} data-testid="vendor-check-badge">
                {vendorCheck.mode === 'INTERNAL' ? `✅ Registered` :
                 vendorCheck.mode === 'APP2APP' ? `📱 App2App` :
                 vendorCheck.mode === 'STATIC' ? `⚠️ STATIC` :
                 `❌ Invalid`}
              </span>
            )}
          </div>
          
          {scanningPayment && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <ZXingScanner onScan={onScanPayment} onClose={()=>setScanningPayment(false)} />
            </div>
          )}
          
          {/* Form Fields */}
          {!(externalToken || externalDeeplink) && (
            <>
              {/* Amount and Message - Always shown */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label>Amount (SEK)</Label>
                  <Input 
                    data-testid="amount-input" 
                    type="number"
                    placeholder="Required" 
                    value={amount} 
                    onChange={(e)=>setAmount(e.target.value)}
                    disabled={!hasActiveParents}
                  />
                </div>
              </div>

              {/* Category Dropdown */}
              <div>
                <Label>Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory} disabled={!hasActiveParents || categories.length === 0}>
                  <SelectTrigger data-testid="category-select-trigger">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.name}>{c.icon} {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Message</Label>
                <Input 
                  data-testid="request-message-input" 
                  placeholder="Optional" 
                  value={message} 
                  onChange={(e)=>setMessage(e.target.value)}
                  disabled={!hasActiveParents}
                />
              </div>
              
              {/* Merchant Swish Number Field - Always shown */}
              <div>
                <Label>Merchant Swish Number (Optional)</Label>
                <Input 
                  data-testid="payee-alias-input" 
                  placeholder="Leave empty for payer's merchant (1236254197)" 
                  value={staticPayee} 
                  onChange={(e)=>setStaticPayee(e.target.value)}
                  disabled={!hasActiveParents}
                />
                {!staticPayee && hasActiveParents && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="buddy-charge-payer-help">
                    💳 Leave empty to charge payer's merchant account
                  </p>
                )}
                {staticPayee && vendorCheck === 'checking' && (
                  <p className="text-xs text-blue-600 mt-1">🔍 Checking vendor registry...</p>
                )}
                {staticPayee && vendorCheck?.mode === 'INTERNAL' && (
                  <p className="text-xs text-green-700 mt-1">
                    ✅ Registered vendor - will use INTERNAL flow with callbacks
                  </p>
                )}
                {staticPayee && vendorCheck?.mode === 'APP2APP' && (
                  <p className="text-xs text-teal-700 mt-1">
                    📱 {vendorCheck.isPhoneNumber ? 'Phone number detected' : 'Unregistered merchant'} - will use App2App flow
                  </p>
                )}
                {staticPayee && vendorCheck?.mode === 'STATIC' && (
                  <p className="text-xs text-yellow-700 mt-1">
                    ⚠️ Unregistered merchant - will use STATIC flow (payer confirms manually)
                  </p>
                )}
                {staticPayee && vendorCheck?.mode === 'INVALID' && (
                  <p className="text-xs text-red-700 mt-1">
                    ❌ {vendorCheck.message || 'Invalid Swish number format'}
                  </p>
                )}
              </div>
            </>
          )}
          
          {(externalToken || externalDeeplink) && (
            <div>
              <Label>Optional note</Label>
              <Input 
                data-testid="external-note-input" 
                value={message} 
                onChange={(e)=>setMessage(e.target.value)} 
                placeholder="e.g., Vendor payment"
                disabled={!hasActiveParents}
              />
            </div>
          )}

          {/* Location Toggle */}
          <div className="flex items-center space-x-2" data-testid="location-toggle-row">
            <Switch 
              id="location-mode" 
              checked={includeLocation}
              onCheckedChange={setIncludeLocation}
              disabled={!hasActiveParents}
              data-testid="location-toggle"
            />
            <Label htmlFor="location-mode" className="flex items-center gap-1">
              <MapPin className="w-4 h-4" /> Attach Location
              {locLoading && <span className="text-xs text-muted-foreground ml-2">(Getting location...)</span>}
              {location && <span className="text-xs text-green-600 ml-2">✓ Attached</span>}
            </Label>
          </div>
          
          {/* Submit Button */}
          <Button 
            data-testid="send-request-button" 
            onClick={handleCreateRequest}
            className="w-full"
            disabled={!hasActiveParents || vendorCheck?.mode === 'INVALID' || (includeLocation && !location)}
          >
            {getButtonText()}
          </Button>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-lg font-semibold mb-3" data-testid="buddy-today-transactions-title">Today's Transactions</h3>
        <div className="space-y-3">
          {todayRequests.map(r => (
            <Card key={r.id} data-testid={`mobpay-today-${r.id}`}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="text-sm flex-1">
                  <div className="font-medium">{r.amount ? `${r.amount} ${r.currency}` : (r.source==='STATIC'?'Static payment':'External token')}</div>
                  <div className="text-xs text-muted-foreground">{formatTime(r.created_at)}</div>
                  {r.vendor_name && <div className="text-xs text-muted-foreground">🏪 {r.vendor_name}</div>}
                  {r.message && <div className="text-xs text-muted-foreground">💬 {r.message}</div>}
                  {r.category_name && <Badge variant="outline" className="mt-1 text-[10px]">{r.category_name}</Badge>}
                </div>
                <span className="ml-auto text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border" data-testid={`mobpay-status-${r.id}`}>{r.status}</span>
              </CardContent>
            </Card>
          ))}
          {todayRequests.length === 0 && <p className="text-sm text-muted-foreground" data-testid="buddy-no-transactions">No transactions today.</p>}
        </div>
      </div>
    </div>
  );
}
